import { CDN_ORACLE_URL } from "config";
import { MANAGE_PRINTERS, POS_PREFERENCES, CASH_MANAGEMENT, MANAGE_RESOURCE, POST_FINAL_INVENTORY_ACTIVITY, EDIT_CUSTOMER_DISPLAY } from "utils/restrictionConstants";
import { BILL_NAV_DESC, BILL_NAV_ID, BILL_NAV_TITLE, KOT_NAV_DESC, KOT_NAV_ID, KOT_NAV_TITLE, POS_NAV_DESC, POS_NAV_ID, POS_NAV_TITLE, POS_SETTING, PRINER_NAV_DESC, PRINTER_NAV_ID, PRINTER_NAV_TITLE, SETTING_TITLE, CASH_REGISTER_NAV_ID } from "utils/sideNavConstants";
import { CASH_REGISTER, FIELD_NOT_ALLOWED_FOR_CASHIER } from "utils/constants";
import { CUSTOMER_ROUTE, INVENTORY_ROUTE, REPORTS_ROUTE, CATALOG_ROUTE, ACCESS_ROUTE, ANALYTICS_ROUTE, MARKETING_ROUTE, SUPPORT_ROUTE, SERVICE_ROUTE, MANAGE_TABLE_ROUTE, CASH_MANAGEMENT_ROUTE, CASHIER_SETTLEMENT_ROUTE, OUTLET_STATUS_ROUTE, ROUTE_CONSTANTS_MAP, ACCOUNT_ROUTE, RISTA_CARDS_ROUTE, CUSTOMER_DISPLAY_SETTINGS_ROUTE, COMPANIES_ROUTE, KIOSK_APP_CONFIGURATION_ROUTE, KIOSK_PAYMENT_CONFIGURATION_ROUTE, KIOSK_CHANNEL_MAPPING_ROUTE } from "../../utils/routeConstants";
import { isMobileView } from "helper/commonHelpers";
import { featureRestrictions } from "helper/featureRestrictionHelper";
import { KIOSK_ENTRY_ENABLED } from "@rista/core/kiosk/featureFlag";

export const USER_ROLES = {
  USER: "User",
  MANAGER: "Manager",
  ADMIN: "Admin",
}

export const PrinterNav: any = {
  id: PRINTER_NAV_ID,
  title: PRINTER_NAV_TITLE,
  desc: PRINER_NAV_DESC,
  role: MANAGE_PRINTERS,
  isPermittedOffline: true 
};

export const PosNav: any = {
  id: POS_NAV_ID,
  title: POS_NAV_TITLE,
  desc: POS_NAV_DESC,
  // role: POS_PREFERENCES,
  isPermittedOffline: false 
};

export const KotNav: any = {
  id: KOT_NAV_ID,
  title: KOT_NAV_TITLE,
  desc: KOT_NAV_DESC,
  role: POS_PREFERENCES,
  isPermittedOffline: false 
};

export const BillNav: any = {
  id: BILL_NAV_ID,
  title: BILL_NAV_TITLE,
  desc: BILL_NAV_DESC,
  isPermittedOffline: false 
};

export const SETTINGS_SUB_MODULES = {
  LABEL_STICKER_SETTING: {
    id: "LABEL_STICKER_SETTING",
    title: "Label Sticker Setting",
    desc: "Label Sticker Setting",
    // Added 2026-09-12. This screen writes `posSettings`, the same document KotNav's screen
    // writes — and KotNav has required POS_PREFERENCES all along. This entry had NO gate at all:
    // not a nav role, and not a `RenderWithRestriction` in `detailView.tsx` either (contrast
    // "pos00", which is wrapped there). apps/mobile's equivalent entry has always carried this
    // role, so the platforms disagreed; the permission is the side that was right.
    role: POS_PREFERENCES,
    isPermittedOffline: false,
  },
  WEIGHING_SCALE_SETTING: {
    id: "WEIGHING_SCALE_SETTING",
    title: "Weighing Machine Setting",
    desc: "Connect a weighing scale to bill loose items by exact weight",
    /*
     * FALSE to match its neighbours, even though the scale is a local serial
     * device that keeps working with no network: the settings list renders
     * nothing but Printer Setup while offline (`LeftMenu/index:466`), so a
     * `true` here would be dead config claiming an availability the list
     * overrides. Making it genuinely offline-available means changing that
     * line, which is shared behaviour and a separate decision.
     */
    isPermittedOffline: false,
    /*
     * ELECTRON ONLY on web (Android gets it from apps/mobile's own SettingNav).
     * Neither a plain browser tab nor the NW.js host is offered the entry: the
     * first has no serial transport at all, and the second is deliberately out
     * of scope for this feature, so either would open a screen that never finds
     * a scale. The gate is `isWeighingScaleSupported()` — a probe of
     * `electronAPI.weighingScale`, not `isElectron()`, so an older shell
     * delivered a newer React bundle over OTA also stays hidden.
     *
     * A serialisable FLAG, resolved by the list at render (`LeftMenu/index`).
     * Not a precomputed boolean, because this module is evaluated once at import
     * and the probe reads `window.electronAPI`, which the Electron preload
     * injects — deciding here races that injection and can never be
     * re-evaluated. And not a predicate either: `setNavigationMenu`
     * `structuredClone`s this whole list, which throws DataCloneError on a
     * function. Everything on these objects must survive a structured clone.
     */
    requiresScaleTransport: true,
  },
}

export const SettingNav: any = {
  title: SETTING_TITLE,
  navLinks: [
    PosNav,
    PrinterNav,
    KotNav,
    SETTINGS_SUB_MODULES.LABEL_STICKER_SETTING,
    // gated at RENDER by its own `requiresScaleTransport` flag — see the entry
    SETTINGS_SUB_MODULES.WEIGHING_SCALE_SETTING,
    // BillNav
  ],
  id: "setting-nav",
  isPermittedOffline: true
};

export const CashManagementNav = {
  title: "Cash Management",
  navLinks: [
    {
      id: "SS01",
      title: "Sale Summary",
      desc: "View and Print Sale Summary",
      activeIcon: `${CDN_ORACLE_URL}/blueDesktopIcon.svg`,
      inActiveIcon: `${CDN_ORACLE_URL}/blackDesktopIcon.svg`,
      role: CASH_MANAGEMENT
    },
    {
      id: CASH_REGISTER_NAV_ID,
      title: "Cash Register",
      desc: "View and manage cash account",
      activeIcon: `${CDN_ORACLE_URL}/cash-register.svg`,
      inActiveIcon: `${CDN_ORACLE_URL}/cash-register-unselected.svg`,
      role: CASH_REGISTER
    },
    { 
      id: "cashier-settlement",
      title: "Cashier Settlement",
      route: CASHIER_SETTLEMENT_ROUTE,
      role: CASH_MANAGEMENT
    },
    { 
      id: "petty-cash-management",
      title: "Petty cash management",
      route: CASH_MANAGEMENT_ROUTE,
      role: CASH_MANAGEMENT
    },
  ],
  isPermittedOffline: false 
};


export const POS_SUB_FEATURES: any = [
  { title: "Item Stock Status", path: "/item-status", id: "item-status", isPermittedOffline: false },
  { title: "Online Outlet Status", id: "outlet-status", route: OUTLET_STATUS_ROUTE , isPermittedOffline: false },
  SettingNav,
  // CashManagementNav,
  {
    id: 'manage-table',
    title: 'Manage Table',
    route: MANAGE_TABLE_ROUTE,
    role: MANAGE_RESOURCE,
    isPermittedOffline: false 
  }
];

export const ACCOUNTING_SUB_FEATURE_TITLES = {
  SALE_SUMMARY: "Sale Summary",
  CASH_REGISTER: "Cash Register",
}

export const INVENTORY_SUB_FEATURES: any = [
    {
    id: "IS01",
    title: "Inventory Module",
    route: INVENTORY_ROUTE,
  },
  {
    id: "IS02",
    title: "Finance Inventory Module",
    route: ROUTE_CONSTANTS_MAP.FINANCE_INVENTORY_MODULE,
    role: POST_FINAL_INVENTORY_ACTIVITY,
  },
]

export const ACCOUNTING_SUB_FEATURES: any = [
  {
    id: "SS01",
    title: ACCOUNTING_SUB_FEATURE_TITLES.SALE_SUMMARY,
    desc: "View and Print Sale Summary",
    activeIcon: `${CDN_ORACLE_URL}/blueDesktopIcon.svg`,
    inActiveIcon: `${CDN_ORACLE_URL}/blackDesktopIcon.svg`,
    role: CASH_MANAGEMENT,
  },
  {
    id: CASH_REGISTER_NAV_ID,
    title: ACCOUNTING_SUB_FEATURE_TITLES.CASH_REGISTER,
    desc: "View and manage cash account",
    activeIcon: `${CDN_ORACLE_URL}/cash-register.svg`,
    inActiveIcon: `${CDN_ORACLE_URL}/cash-register-unselected.svg`,
    role: CASH_REGISTER,
    accessRoles: [USER_ROLES.USER]
  },
  {
    title: "Cash Management",
    id: "petty-cash-management",
    route: ROUTE_CONSTANTS_MAP.CASH_MANAGEMENT_ROUTE,
    isPermittedOffline: false,
    role: CASH_MANAGEMENT,
    accessRoles: [USER_ROLES.MANAGER, USER_ROLES.ADMIN]
  },
  {
    title: "Cashier Settlement",
    id: "cashier-settlement",
    route: ROUTE_CONSTANTS_MAP.CASHIER_SETTLEMENT_ROUTE,
    isPermittedOffline: false,
    role: CASH_MANAGEMENT,
    accessRoles: [USER_ROLES.MANAGER, USER_ROLES.ADMIN]
  },
  {
    title: "P&L Statement",
    id: "pnl-statement",
    route: ROUTE_CONSTANTS_MAP.PNL_STATEMENT,
    isPermittedOffline: false,
    accessRoles: [USER_ROLES.MANAGER, USER_ROLES.ADMIN]
  },
  {
    title: "Sales Audit",
    id: "sales-audit",
    route: ROUTE_CONSTANTS_MAP.SALES_AUDIT,
    isPermittedOffline: false,
    accessRoles: [USER_ROLES.ADMIN]
  },
  {
    title: "Account Summary",
    id: "account-summary",
    route: ROUTE_CONSTANTS_MAP.ACCOUNT_SUMMARY,
    isPermittedOffline: false,
    accessRoles: [USER_ROLES.MANAGER, USER_ROLES.ADMIN]
  },
];

/** The Kiosk group's id. */
export const KIOSK_NAV_ID = "kiosk";

/** The Kiosk group's items — identical to mobile's `KIOSK_SUB_FEATURES`. */
export const KIOSK_SUB_FEATURES: any = [
  {
    title: "Start Kiosk",
    id: "kiosk-self-ordering",
    // Hidden until payment, branding and channels are set up (`@rista/core/kiosk/setup`).
    requiresKioskSetup: true,
    route: ROUTE_CONSTANTS_MAP.KIOSK_ROUTE,
    isPermittedOffline: true,
  },
  {
    title: "Branding",
    id: "kiosk-branding",
    route: KIOSK_APP_CONFIGURATION_ROUTE,
    isPermittedOffline: true,
    // Admin only, as on mobile.
    accessRoles: [USER_ROLES.ADMIN],
  },
  {
    title: "Channels",
    id: "kiosk-channels",
    route: KIOSK_CHANNEL_MAPPING_ROUTE,
    isPermittedOffline: true,
  },
  {
    title: "Payment",
    id: "kiosk-payment",
    route: KIOSK_PAYMENT_CONFIGURATION_ROUTE,
    isPermittedOffline: true,
  },
];

export const NAVIGATION_MENU_LIST: any = [
  {
    module: "Sale",
    licences: [
      "SEP",
      "SOL",
      "SLT",
      "SLF",
      "RHP",
      "POO"
    ],
    roles: [
      "User",
      "Manager",
      "Admin"
    ],
    isShow: true,
    title: "Billing & Order",
    route: ROUTE_CONSTANTS_MAP.NEW_ORDER_ROUTE,
    pageHeading: POS_SETTING,
    id: "pos",
    icon: `${CDN_ORACLE_URL}/billing_order.svg`,
    featuresList: POS_SUB_FEATURES,
    isPermittedOffline: true
  },
  {
    // Self-Ordering Kiosk — see docs/KIOSK_ARCHITECTURE.md. Identical to the mobile entry
    // (apps/mobile/src/commonComponents/LeftMenu/menuItemsData.ts). Its OWN licence module, like
    // Marketing: shown under the KIOSK licence (KSK) and under RHP (2026-10-13), and switched to
    // from "other licences", where `LICENSE_LANDING_ROUTE.KSK` lands on Home. A GROUP, like Accounting: the
    // kiosk itself, its branding, its channel mapping and its payment set-up are the
    // `KIOSK_SUB_FEATURES` under it. Home has no sub-item of its own — a click on the group's
    // title opens it.
    //
    // OPEN decision before release (§12): whether a cashier ("User") may put a device into
    // kiosk mode at all.
    module: "Kiosk",
    licences: [
      "KSK",
      "RHP"
    ],
    roles: [
      "User",
      "Manager",
      "Admin"
    ],
    // OFF for release — the kiosk flow is incomplete. `@rista/core/kiosk/featureFlag`
    // is the whole switch; flip it there, not here.
    isShow: KIOSK_ENTRY_ENABLED,
    title: "Kiosk",
    // Where a click on the group's own title goes: the kiosk Home page.
    route: ROUTE_CONSTANTS_MAP.KIOSK_HOME_ROUTE,
    id: KIOSK_NAV_ID,
    icon: `${CDN_ORACLE_URL}/billing_order.svg`,
    featuresList: KIOSK_SUB_FEATURES,
    // Not offered offline: kiosk offline mode is not supported yet (KIOSK_ARCHITECTURE §8).
    isPermittedOffline: false
  },
  {
    module: "Sale",
    licences: [
      "SEP",
      "SOL",
      "SLT",
      "SLF",
      "RHP",
    ],
    roles: [
      "User",
      "Manager",
      "Admin"
    ],
    isShow: true,
    title: "Customers",
    route: CUSTOMER_ROUTE,
    icon: `${CDN_ORACLE_URL}/customers.svg`,
    isPermittedOffline: false
  },
    {
    module: "Sale",
    licences: [
      "SEP",
      "SOL",
      "SLT",
      "SLF",
      "RHP",
    ],
    roles: [
      "User",
      "Manager",
      "Admin"
    ],
    isShow: true,
    title: "Companies",
    route: COMPANIES_ROUTE,
    icon: `${CDN_ORACLE_URL}/ic-company-sm.svg`,
    isPermittedOffline: false
  },
   {
    module: "Sale",
    licences: [
      "SEP",
      "SOL",
      "SLT",
      "SLF",
      "RHP",
    ],
    roles: [
      "User",
      "Manager",
      "Admin"
    ],
    isShow: true,
    title: "Rista cards",
    route: RISTA_CARDS_ROUTE,
    icon: `${CDN_ORACLE_URL}/rista_cards.svg`,
    isPermittedOffline: false
  },
 {
    module: "Sale",
    licences: [
      "SEP",
      "SOL",
      "SLT",
      "SLF",
      "RHP",
    ],
    roles: [
      "User",
      "Manager",
      "Admin"
    ],
    isShow: true,
    title: "Customer Display",
    route: CUSTOMER_DISPLAY_SETTINGS_ROUTE,
    icon: `${CDN_ORACLE_URL}/ic-customer-display.svg`,
    // shown only when preferences/user -> allowedFeatures contains EDIT_CUSTOMER_DISPLAY
    role: EDIT_CUSTOMER_DISPLAY,
    isPermittedOffline: false
  },
  {
    module: "Sale",
    licences: [
      "SEP",
      "SOL",
      "SLT",
      "SLF",
      "RHP",
    ],
    roles: [
      "User",
      "Manager",
      "Admin"
    ],
    isShow: true,
    title: "Reservations",
    route: ROUTE_CONSTANTS_MAP.RESERVATIONS_ROUTE,
    icon: `${CDN_ORACLE_URL}/reservations.svg`,
    isPermittedOffline: false
  },
  {
    module: "Inventory",
    licences: [
      "RHP",
      "SLF",
      "SEP",
      "INV"
    ],
    roles: [
      "Manager",
      "Admin"
    ],
    isShow: true,
    title: "Inventory",
    id: "inventory",
    icon: `${CDN_ORACLE_URL}/inventory_management.svg`,
    featuresList: INVENTORY_SUB_FEATURES,
    isPermittedOffline: false
  },
  {
    module: "Analytics",
    licences: [
      "RHP",
      "SLF",
      "SOL",
      "SLT",
      "SEP",
      "ALT",
      "MKT"
    ],
    roles: [
      "Manager",
      "Admin"
    ],
    isShow: true,
    title: "Analytics",
    route: ANALYTICS_ROUTE,
    icon: `${CDN_ORACLE_URL}/analytics.svg`,
    isPermittedOffline: false
  },
  {
    module: "Reports",
    licences: [
      "RHP",
      "SLF",
      "SOL",
      "SLT",
      "SEP",
      "ALT",
      "MKT"
    ],
    roles: [
      "Manager",
      "Admin"
    ],
    isShow: true,
    title: "Reports",
    route: REPORTS_ROUTE,
    icon: `${CDN_ORACLE_URL}/report.svg`,
    isPermittedOffline: false
  },
  {
    module: "Accounting",
    licences: [
      "SEP",
      "SLT",
      "RHP"
    ],
    roles: [
      USER_ROLES.ADMIN,
      USER_ROLES.MANAGER,
      USER_ROLES.USER,
    ],
    isShow: true,
    title: "Accounting",
    route: ROUTE_CONSTANTS_MAP.ACCOUNTING_ROUTE,
    pageHeading: POS_SETTING,
    id: "accounting",
    icon: `${CDN_ORACLE_URL}/billing_order.svg`,
    featuresList: ACCOUNTING_SUB_FEATURES,
    isPermittedOffline: false
  },
  {
    module: "Catalog",
    licences: [
      "RHP",
      "SLF",
      "SOL",
      "SEP",
      "SLT"
    ],
    roles: [
      "Admin"
    ],
    isShow: !isMobileView(),
    title: "Catalog",
    route: CATALOG_ROUTE,
    icon: `${CDN_ORACLE_URL}/catalogue.svg`,
    isPermittedOffline: false
  },
  {
    module: "Settings",
    licences: [
      LICENCE_CODES.RHP,
      LICENCE_CODES.SLF,
      LICENCE_CODES.SOL,
      LICENCE_CODES.SEP,
      LICENCE_CODES.SLT,
    ],
    roles: [USER_ROLES.ADMIN],
    isShow: true,
    title: "Business Settings",
    route: ROUTE_CONSTANTS_MAP.SETTINGS_ROUTE,
    // icon: `${CDN_ORACLE_URL}/settings.svg`,
    isPermittedOffline: false,
  },
  {
    module: "Access",
    licences: [
      "RHP",
      "SLF",
      "SOL",
      "SEP",
      "SLT"
    ],
    roles: [
      "Manager",
      "Admin"
    ],
    isShow: true,
    title: "Access",
    route: ACCESS_ROUTE,
    icon: `${CDN_ORACLE_URL}/access.svg`,
    isPermittedOffline: false
  },
  {
    module: "Account",
    licences: [
      "RHP",
      "SLF",
      "SOL",
      "SEP",
      "SLT"
    ],
    roles: [
      "Admin"
    ],
    isShow: true,
    title: "Account",
    route: ACCOUNT_ROUTE,
    icon: `${CDN_ORACLE_URL}/access.svg`,
    isPermittedOffline: false
  },
  {
    module: "Marketing",
    licences: [
      "RHP",
      "MKT"
    ],
    roles: [
      "Manager",
      "Admin"
    ],
    isShow: true,
    title: "Marketing",
    route: MARKETING_ROUTE,
    icon: `${CDN_ORACLE_URL}/marketing.svg`,
    isPermittedOffline: false
  },
  {
    module: "Support",
    licences: [
      "RHP",
      "SPT"
    ],
    roles: [
      "User",
      "Manager",
      "Admin"
    ],
    isShow: true,
    title: "Support",
    route: SUPPORT_ROUTE,
    icon: `${CDN_ORACLE_URL}/support.svg`,
    isPermittedOffline: false
  },
  {
    module: "Ticket Display",
    licences: [
      "POS",
      "RHP",
    ],
    roles: [
      "User",
      "Admin",
      "Manager",
    ],
    isShow: true,
    title: "Ticket Display",
    route: SERVICE_ROUTE,
    icon: `${CDN_ORACLE_URL}/ticket_display.svg`,
    isPermittedOffline: false
  }
];


export const OTHER_LICENCES: any = {
  "INV": "INVENTORY",
  "MKT": "MARKETING",
  "ALT": "ANALYTICS",
  "SEP": "SALES",
  "SPT": "SUPPORT",
  "POS": "TICKET DISPLAY",
  "SLT": "SALES",
  "SLF": "SALES",
  "SOL": "SALES",
  "POO": "ORDERING",
  "RHP":"RISTA SUPPORT",
  // POD - removed
  "KSK": "KIOSK",
};

/** Moved to `@rista/core/commonComponents/LeftMenu/menuItemsData`; re-exported so importers of this file are unchanged. Nothing in this file's own body reads it. */
export { LICENSE_LANDING_ROUTE } from "@rista/core/commonComponents/LeftMenu/menuItemsData";

export const USER_ROLE = 'User'

export const checkCashierRestrictField = (role:string , val = '') => {
  return FIELD_NOT_ALLOWED_FOR_CASHIER.includes(val) && (featureRestrictions(CASH_MANAGEMENT) || featureRestrictions(CASH_REGISTER)) && role === USER_ROLE
}

// List of license types that support order flow
export const LICENSES_WITH_ORDER_FLOW = [
  LICENCE_CODES.SOL,
  LICENCE_CODES.SEP,
  LICENCE_CODES.SLT,
  LICENCE_CODES.SLF,
  LICENCE_CODES.RHP,
  LICENCE_CODES.POO,
] as const;

// routes for order flow modules
export const ORDER_FLOW_ROUTES = [
  ROUTE_CONSTANTS_MAP.NEW_ORDER_ROUTE,
  ROUTE_CONSTANTS_MAP.MY_ORDER_ROUTE,
  ROUTE_CONSTANTS_MAP.TABLE_VIEW_ROUTE,
  ROUTE_CONSTANTS_MAP.RESERVATIONS_ROUTE,
] as const;

// Moved to @rista/core/commonComponents/LeftMenu/menuItemsData (shared.ts — byte-identical in
// both apps, unlike LICENSE_LANDING_ROUTE whose values are each platform's own route vocabulary).
// 🔴 IMPORTED as well as re-exported: this file's own body uses LICENCE_CODES in the menu rows
// below, and an `export ... from` re-export creates NO local binding (ledger rule 5 / hazard 30).
// The first attempt here was the re-export alone and web tsc caught it as 11 x TS2304 — in a
// `.js` file the same mistake would have shipped as a runtime ReferenceError.
import { LICENCE_CODES } from "@rista/core/commonComponents/LeftMenu/menuItemsData";
export { LICENCE_CODES };
